class BravoAimController extends HTMLElement {
  static #DEFAULT_AIM_COLOR = '#ffffff';
  static #DEFAULT_AIM_RGB = '255, 255, 255';

  /** @type {HTMLDivElement | null} */ #cursor = null;
  /** @type {HTMLElement | null} */ #crosshair = null;
  /** @type {HTMLDivElement | null} */ #linesOverlay = null;
  /** @type {HTMLDivElement | null} */ #cornersOverlay = null;
  /** @type {HTMLElement | null} */ #currentTarget = null;
  /** @type {number | null} */ #animFrame = null;
  /** @type {MutationObserver | null} */ #domObserver = null;
  #isFine = false;

  // ---- Scroll handler (fine pointer — mouse doesn't fire mousemove on scroll) ----

  #handleScroll = () => {
    if (this.#currentTarget) {
      this.#positionOverlay(this.#currentTarget);
    }
  };

  // ---- Mouse handlers (fine pointer only) ----

  #handleMouseMove = (/** @type {MouseEvent} */ e) => {
    if (this.#animFrame) cancelAnimationFrame(this.#animFrame);
    this.#animFrame = requestAnimationFrame(() => {
      this.#cursor?.style.setProperty('--cx', `${e.clientX}px`);
      this.#cursor?.style.setProperty('--cy', `${e.clientY}px`);
      if (this.#currentTarget) {
        this.#positionOverlay(this.#currentTarget);
      }
    });
  };

  #handleDocLeave = () => {
    if (this.#cursor) this.#cursor.style.opacity = '0';
  };

  #handleDocEnter = () => {
    if (this.#cursor) this.#cursor.style.opacity = '1';
  };

  // mouseover/mouseout bubble — reliable for delegation with closest()
  #handleMouseOver = (/** @type {MouseEvent} */ e) => {
    const target = /** @type {HTMLElement | null} */ (e.target)?.closest('[data-aim]');
    if (!target || target === this.#currentTarget) return;
    this.#currentTarget = /** @type {HTMLElement} */ (target);
    this.#showOverlay(/** @type {HTMLElement} */ (target));
  };

  #handleMouseOut = (/** @type {MouseEvent} */ e) => {
    const target = /** @type {HTMLElement | null} */ (e.target)?.closest('[data-aim]');
    if (!target) return;
    if (target.contains(/** @type {Node} */ (e.relatedTarget))) return;
    this.#currentTarget = null;
    this.#hideOverlay();
  };

  // ---- Fire animation (both pointer types) ----

  #triggerFire = () => {
    // Cursor crosshair: scale collapse → spring back
    if (this.#crosshair) {
      this.#crosshair.classList.remove('is-fired');
      void this.#crosshair.offsetWidth; // force reflow to restart animation
      this.#crosshair.classList.add('is-fired');
      this.#crosshair.addEventListener(
        'animationend',
        () => this.#crosshair?.classList.remove('is-fired'),
        { once: true }
      );
    }
    // Corners: fast squeeze in, then spring back when class removed
    this.#cornersOverlay?.classList.add('is-fired');
    setTimeout(() => this.#cornersOverlay?.classList.remove('is-fired'), 80);
    // Lines: quick blink
    this.#linesOverlay?.classList.add('is-fired');
    setTimeout(() => this.#linesOverlay?.classList.remove('is-fired'), 260);
  };

  #handleFire = (/** @type {MouseEvent} */ e) => {
    if (!/** @type {HTMLElement | null} */ (e.target)?.closest('[data-aim]')) return;
    this.#triggerFire();
  };

  // ---- Touch handlers (coarse pointer only) ----

  #handleTouchStart = (/** @type {TouchEvent} */ e) => {
    const target = /** @type {HTMLElement | null} */ (e.target)?.closest('[data-aim]');
    if (!target) return;
    this.#currentTarget = /** @type {HTMLElement} */ (target);
    this.#showOverlay(/** @type {HTMLElement} */ (target));
    this.#triggerFire();
  };

  #handleTouchEnd = () => {
    if (!this.#currentTarget) return;
    this.#currentTarget = null;
    this.#hideOverlay();
  };

  #handleDomMutations = () => {
    if (this.#currentTarget && !this.#currentTarget.isConnected) {
      this.#currentTarget = null;
      this.#hideOverlay();
    }
  };

  // ---- Lifecycle ----

  connectedCallback() {
    this.#isFine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    this.#buildOverlays();

    if (this.#isFine) {
      // DISABLED: custom cursor — comment out to re-enable
      // this.#buildCursor();
      // document.body.classList.add('bravo-cursor-active');
      // document.addEventListener('mousemove', this.#handleMouseMove);
      // document.documentElement.addEventListener('mouseleave', this.#handleDocLeave);
      // document.documentElement.addEventListener('mouseenter', this.#handleDocEnter);
      document.addEventListener('mouseover', this.#handleMouseOver);
      document.addEventListener('mouseout', this.#handleMouseOut);
      document.addEventListener('mousedown', this.#handleFire);
    } else {
      // Coarse pointer (touch): overlay animates on tap but no crosshair cursor is built
      document.addEventListener('touchstart', this.#handleTouchStart, { passive: true });
      document.addEventListener('touchend',   this.#handleTouchEnd,   { passive: true });
      document.addEventListener('touchcancel', this.#handleTouchEnd,  { passive: true });
    }
    window.addEventListener('scroll', this.#handleScroll, { passive: true, capture: true });

    this.#domObserver = new MutationObserver(this.#handleDomMutations);
    this.#domObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }

  disconnectedCallback() {
    if (this.#isFine) {
      // DISABLED: custom cursor cleanup — comment out to re-enable
      // document.body.classList.remove('bravo-cursor-active');
      // document.removeEventListener('mousemove', this.#handleMouseMove);
      // document.documentElement.removeEventListener('mouseleave', this.#handleDocLeave);
      // document.documentElement.removeEventListener('mouseenter', this.#handleDocEnter);
      // if (this.#animFrame) cancelAnimationFrame(this.#animFrame);
      // this.#cursor?.remove();
      document.removeEventListener('mouseover', this.#handleMouseOver);
      document.removeEventListener('mouseout', this.#handleMouseOut);
      document.removeEventListener('mousedown', this.#handleFire);
    } else {
      document.removeEventListener('touchstart', this.#handleTouchStart);
      document.removeEventListener('touchend',   this.#handleTouchEnd);
      document.removeEventListener('touchcancel', this.#handleTouchEnd);
    }
    window.removeEventListener('scroll', this.#handleScroll, { capture: true });
    this.#domObserver?.disconnect();
    this.#domObserver = null;
    this.#linesOverlay?.remove();
    this.#cornersOverlay?.remove();
  }

  // ---- DOM builders ----

  #buildCursor() {
    this.#cursor = document.createElement('div');
    this.#cursor.className = 'bravo-cursor';
    this.#cursor.setAttribute('aria-hidden', 'true');
    this.#cursor.innerHTML = `
      <div class="bravo-cursor__crosshair">
        <div class="bravo-cursor__arm bravo-cursor__arm--top"></div>
        <div class="bravo-cursor__arm bravo-cursor__arm--bottom"></div>
        <div class="bravo-cursor__arm bravo-cursor__arm--left"></div>
        <div class="bravo-cursor__arm bravo-cursor__arm--right"></div>
      </div>
    `;
    this.#crosshair = this.#cursor.querySelector('.bravo-cursor__crosshair');
    document.body.appendChild(this.#cursor);
  }

  #buildOverlays() {
    this.#linesOverlay = document.createElement('div');
    this.#linesOverlay.className = 'bravo-aim-lines';
    this.#linesOverlay.setAttribute('aria-hidden', 'true');
    this.#linesOverlay.innerHTML = `
      <div class="bravo-aim-lines__h"></div>
      <div class="bravo-aim-lines__v"></div>
    `;
    document.body.appendChild(this.#linesOverlay);

    this.#cornersOverlay = document.createElement('div');
    this.#cornersOverlay.className = 'bravo-aim-corners';
    this.#cornersOverlay.setAttribute('aria-hidden', 'true');
    this.#cornersOverlay.innerHTML = `
      <div class="bravo-aim-corners__tl"></div>
      <div class="bravo-aim-corners__tr"></div>
      <div class="bravo-aim-corners__bl"></div>
      <div class="bravo-aim-corners__br"></div>
    `;
    document.body.appendChild(this.#cornersOverlay);
  }

  // ---- Overlay positioning ----

  #positionOverlay(/** @type {HTMLElement} */ target) {
    const rect = target.getBoundingClientRect();
    const megaPanel = target.closest('.header-bravo__mega-panel');
    const section = megaPanel || target.closest('section, [data-section-id]') || document.documentElement;
    const sectionPad = parseFloat(getComputedStyle(section).getPropertyValue('--aim-target-pad'));
    const isHeroPlp = Boolean(target.closest('.hero-plp'));
    const pad = Number.isFinite(sectionPad) ? sectionPad : (isHeroPlp ? 4 : 8);

    const left = rect.left - pad;
    const top = rect.top - pad;
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const w = rect.width + pad * 2;
    const h = rect.height + pad * 2;
    const sr = section.getBoundingClientRect();

    const [aimColor, aimColorRgb] = this.#getAimColor(target);

    [this.#linesOverlay, this.#cornersOverlay].forEach((el) => {
      if (!el) return;
      el.style.setProperty('--aim-pad', `${pad}px`);
      el.style.setProperty('--aim-left', `${left}px`);
      el.style.setProperty('--aim-top', `${top}px`);
      el.style.setProperty('--aim-cx', `${cx}px`);
      el.style.setProperty('--aim-cy', `${cy}px`);
      el.style.setProperty('--aim-w', `${w}px`);
      el.style.setProperty('--aim-h', `${h}px`);
      el.style.setProperty('--section-left', `${sr.left}px`);
      el.style.setProperty('--section-top', `${sr.top}px`);
      el.style.setProperty('--section-w', `${sr.width}px`);
      el.style.setProperty('--section-h', `${sr.height}px`);
      el.style.setProperty('--aim-color', aimColor);
      el.style.setProperty('--aim-color-rgb', aimColorRgb);
    });
  }

  // mix-blend-mode: difference handles contrast automatically — always use white
  #getAimColor(/** @type {HTMLElement} */ _target) {
    return [BravoAimController.#DEFAULT_AIM_COLOR, BravoAimController.#DEFAULT_AIM_RGB];
  }

  #showOverlay(/** @type {HTMLElement} */ target) {
    const isInKlaviyoModal = Boolean(target.closest('.bravo-klaviyo-modal'));
    [this.#linesOverlay, this.#cornersOverlay].forEach((el) => {
      if (!el) return;
      el.classList.toggle('is-modal-context', isInKlaviyoModal);
    });
    this.#positionOverlay(target);
    this.#linesOverlay?.classList.add('is-active');
    this.#cornersOverlay?.classList.add('is-active');
  }

  #hideOverlay() {
    this.#linesOverlay?.classList.remove('is-active');
    this.#cornersOverlay?.classList.remove('is-active');
    this.#linesOverlay?.classList.remove('is-modal-context');
    this.#cornersOverlay?.classList.remove('is-modal-context');
  }
}

customElements.define('bravo-aim-controller', BravoAimController);

// Mount on all devices — the controller branches internally between
// fine pointer (mouse) and coarse pointer (touch) behaviour.
function mountBravoAim() {
  const controller = document.createElement('bravo-aim-controller');
  document.body.appendChild(controller);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', mountBravoAim);
} else {
  mountBravoAim();
}
